import { createApi } from '@reduxjs/toolkit/query/react';

import { normalizeReport } from '@/slices/reports/reports.normalizer';
import {
  AddReportsWithCampaignsInput,
  AddReportWithNewCampaignInput,
  CampaignsResponse,
  ChooseStandardRequirementsInput,
  Datapoint,
  DisclosureRequirement,
  RemoveReportWithCampaignInput,
  Report,
  ReportCampaign,
  ReportResponse,
  ReportsResponse,
  ReportStandardRequirementsResponse,
  StandardRequirement,
} from '@/slices/reports/reports.types';

import { symfonyBaseQuery } from '@/services/SymfonyClient';

const toTimestamp = (value: string): number => {
  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareReportsForList = (left: Report, right: Report): number => {
  const standardSlugDiff = left.slug.localeCompare(right.slug);
  if (standardSlugDiff !== 0) {
    return standardSlugDiff;
  }

  const campaignOrderDiff = compareCampaignsForList(
    left.campaign,
    right.campaign,
  );
  if (campaignOrderDiff !== 0) {
    return campaignOrderDiff;
  }

  return right.id.localeCompare(left.id);
};

const compareCampaignsForList = (
  left: ReportCampaign,
  right: ReportCampaign,
): number => {
  const toDateDiff = toTimestamp(right.toDate) - toTimestamp(left.toDate);
  if (toDateDiff !== 0) {
    return toDateDiff;
  }

  const fromDateDiff = toTimestamp(right.fromDate) - toTimestamp(left.fromDate);
  if (fromDateDiff !== 0) {
    return fromDateDiff;
  }

  return right.id.localeCompare(left.id);
};

const getStandardRequirementsCacheId = (
  enterpriseId: string,
  reportId: string,
): string => `${enterpriseId}:${reportId}`;

export const getDisclosureRequirementCacheId = (
  enterpriseId: string,
  reportId: string,
  disclosureRequirementIdentifier: string,
): string => `${enterpriseId}:${reportId}:${disclosureRequirementIdentifier}`;

const reportsApi = createApi({
  baseQuery: symfonyBaseQuery({
    baseUrl: '/api/enterprises',
  }),
  reducerPath: 'reports',
  tagTypes: [
    'Reports',
    'Campaigns',
    'StandardRequirements',
    'DisclosureRequirements',
  ],
  endpoints: (build) => ({
    getCampaignsByEnterprise: build.query<
      ReportCampaign[],
      {
        enterpriseId: string;
      }
    >({
      query: ({ enterpriseId }) => ({
        url: `/${enterpriseId}/campaigns`,
        method: 'GET',
      }),
      transformResponse: (response: CampaignsResponse) =>
        [...(response.campaigns ?? [])].sort(compareCampaignsForList),
      providesTags: (_result, _error, { enterpriseId }) => [
        { type: 'Campaigns', id: enterpriseId },
      ],
    }),
    getReportsByEnterprise: build.query<
      Report[],
      {
        enterpriseId: string;
      }
    >({
      query: ({ enterpriseId }) => ({
        url: `/${enterpriseId}/reports`,
        method: 'GET',
      }),
      transformResponse: (response: ReportsResponse) =>
        (response.reports ?? [])
          .map(normalizeReport)
          .sort(compareReportsForList),
      providesTags: (_result, _error, { enterpriseId }) => [
        { type: 'Reports', id: enterpriseId },
      ],
    }),
    getReportByEnterprise: build.query<
      Report,
      {
        enterpriseId: string;
        reportId: string;
      }
    >({
      query: ({ enterpriseId, reportId }) => ({
        url: `/${enterpriseId}/reports/${reportId}`,
        method: 'GET',
      }),
      transformResponse: (response: Report) => normalizeReport(response),
      providesTags: (_result, _error, { enterpriseId, reportId }) => [
        { type: 'Reports', id: `${enterpriseId}:${reportId}` },
      ],
    }),
    getStandardRequirementsByReport: build.query<
      StandardRequirement[],
      {
        enterpriseId: string;
        reportId: string;
      }
    >({
      query: ({ enterpriseId, reportId }) => ({
        url: `/${enterpriseId}/reports/${reportId}/standard-requirements`,
        method: 'GET',
      }),
      transformResponse: (response: ReportStandardRequirementsResponse) =>
        response.standardRequirements ?? [],
      providesTags: (result, _error, { enterpriseId, reportId }) => {
        const sharedTagId = getStandardRequirementsCacheId(
          enterpriseId,
          reportId,
        );

        const disclosureRequirementTags: Array<{
          type: 'DisclosureRequirements';
          id: string;
        }> = (result ?? []).flatMap((standardRequirement) =>
          standardRequirement.disclosureRequirements
            .map((disclosureRequirement) => {
              const identifier =
                disclosureRequirement?.standardItemLink?.slug ||
                disclosureRequirement.id ||
                null;

              if (!identifier) {
                return null;
              }

              return {
                type: 'DisclosureRequirements',
                id: getDisclosureRequirementCacheId(
                  enterpriseId,
                  reportId,
                  identifier,
                ),
              };
            })
            .filter(
              (tag): tag is { type: 'DisclosureRequirements'; id: string } =>
                tag !== null,
            ),
        );

        return [
          { type: 'StandardRequirements', id: sharedTagId },
          ...disclosureRequirementTags,
        ];
      },
    }),
    getDisclosureRequirementByReport: build.query<
      DisclosureRequirement,
      {
        enterpriseId: string;
        reportId: string;
        disclosureRequirementIdentifier: string;
      }
    >({
      query: ({ enterpriseId, reportId, disclosureRequirementIdentifier }) => ({
        url: `/${enterpriseId}/reports/${reportId}/disclosure-requirements/${disclosureRequirementIdentifier}`,
        method: 'GET',
      }),
      transformResponse: (response: DisclosureRequirement) => response,
      async onQueryStarted(
        { enterpriseId, reportId, disclosureRequirementIdentifier },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: disclosureRequirement } = await queryFulfilled;

          const identifier = disclosureRequirement.id;

          if (!identifier || identifier === disclosureRequirementIdentifier) {
            return;
          }

          await dispatch(
            reportsApi.util.upsertQueryData(
              'getDisclosureRequirementByReport',
              {
                enterpriseId,
                reportId,
                disclosureRequirementIdentifier: identifier,
              },
              disclosureRequirement,
            ),
          );
        } catch {
          // Keep cache untouched when the query fails.
        }
      },
      providesTags: (
        _result,
        _error,
        { enterpriseId, reportId, disclosureRequirementIdentifier },
      ) => [
        {
          type: 'DisclosureRequirements',
          id: getDisclosureRequirementCacheId(
            enterpriseId,
            reportId,
            disclosureRequirementIdentifier,
          ),
        },
        {
          type: 'StandardRequirements',
          id: getStandardRequirementsCacheId(enterpriseId, reportId),
        },
      ],
    }),
    getDisclosureRequirementDatapointsByReport: build.query<
      Datapoint[],
      {
        enterpriseId: string;
        reportId: string;
        disclosureRequirementId: string;
      }
    >({
      query: ({ enterpriseId, reportId, disclosureRequirementId }) => ({
        url: `/${enterpriseId}/reports/${reportId}/disclosure-requirements/${disclosureRequirementId}/datapoints`,
        method: 'GET',
      }),
      transformResponse: (response: { datapoints?: Datapoint[] }) =>
        response.datapoints ?? [],
      providesTags: (
        _result,
        _error,
        { enterpriseId, reportId, disclosureRequirementId },
      ) => [
        {
          type: 'DisclosureRequirements',
          id: getDisclosureRequirementCacheId(
            enterpriseId,
            reportId,
            disclosureRequirementId,
          ),
        },
      ],
    }),
    chooseStandardRequirements: build.mutation<
      StandardRequirement[],
      ChooseStandardRequirementsInput
    >({
      query: ({ enterpriseId, reportId, standardsRequirements }) => ({
        url: `/${enterpriseId}/reports/${reportId}/standard-requirements`,
        method: 'PATCH',
        data: {
          standardsRequirements,
        },
      }),
      transformResponse: (response: ReportStandardRequirementsResponse) =>
        response.standardRequirements ?? [],
      async onQueryStarted(
        { enterpriseId, reportId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedStandardRequirements } = await queryFulfilled;

          dispatch(
            reportsApi.util.updateQueryData(
              'getStandardRequirementsByReport',
              { enterpriseId, reportId },
              (draft) => {
                draft.splice(0, draft.length, ...updatedStandardRequirements);
              },
            ),
          );
        } catch {
          // Keep cache untouched when mutation fails.
        }
      },
      invalidatesTags: (_result, _error, { enterpriseId, reportId }) => [
        { type: 'StandardRequirements', id: `${enterpriseId}:${reportId}` },
      ],
    }),
    addReportsWithCampaigns: build.mutation<
      Report[],
      AddReportsWithCampaignsInput
    >({
      query: ({ enterpriseId, standardSlug, campaignIds }) => ({
        url: `/${enterpriseId}/reports/with-campaigns`,
        method: 'POST',
        data: {
          standardSlug,
          campaignIds,
        },
      }),
      transformResponse: (response: ReportsResponse) =>
        (response.reports ?? []).map(normalizeReport),
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: createdOrRestoredReports } = await queryFulfilled;

          dispatch(
            reportsApi.util.updateQueryData(
              'getReportsByEnterprise',
              { enterpriseId },
              (draft) => {
                const mergedReportsById = new Map(
                  draft.map((report) => [report.id, report]),
                );

                for (const report of createdOrRestoredReports) {
                  mergedReportsById.set(report.id, report);
                }

                draft.splice(
                  0,
                  draft.length,
                  ...Array.from(mergedReportsById.values()).sort(
                    compareReportsForList,
                  ),
                );
              },
            ),
          );
        } catch {
          // Keep cache untouched when mutation fails.
        }
      },
    }),
    addReportWithNewCampaign: build.mutation<
      Report,
      AddReportWithNewCampaignInput
    >({
      query: ({ enterpriseId, standardSlug, campaign }) => ({
        url: `/${enterpriseId}/reports/with-new-campaign`,
        method: 'POST',
        data: {
          standardSlug,
          campaign,
        },
      }),
      transformResponse: (response: ReportResponse) =>
        normalizeReport(response.report),
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: createdReport } = await queryFulfilled;

          dispatch(
            reportsApi.util.updateQueryData(
              'getReportsByEnterprise',
              { enterpriseId },
              (draft) => {
                const mergedReportsById = new Map(
                  draft.map((report) => [report.id, report]),
                );
                mergedReportsById.set(createdReport.id, createdReport);

                draft.splice(
                  0,
                  draft.length,
                  ...Array.from(mergedReportsById.values()).sort(
                    compareReportsForList,
                  ),
                );
              },
            ),
          );
        } catch {
          // Keep cache untouched when mutation fails.
        }
      },
      invalidatesTags: (_result, _error, { enterpriseId }) => [
        { type: 'Campaigns', id: enterpriseId },
      ],
    }),
    removeReportWithCampaign: build.mutation<
      void,
      RemoveReportWithCampaignInput
    >({
      query: ({ enterpriseId, campaignId, reportId }) => ({
        url: `/${enterpriseId}/reports/${reportId}/campaigns/${campaignId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { enterpriseId, reportId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          reportsApi.util.updateQueryData(
            'getReportsByEnterprise',
            { enterpriseId },
            (draft) => {
              const index = draft.findIndex((report) => report.id === reportId);

              if (index >= 0) {
                draft.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export default reportsApi;
